import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { 
  ShoppingCart, 
  Sparkle, 
  Check, 
  X, 
  Warning,
  Wallet,
  Target,
  Clock,
  TrendDown
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { authPost } from '../utils/api';

const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', INR: '₹', JPY: '¥'
};

export default function Affordability() {
  const { user } = useAuth();
  const [itemName, setItemName] = useState('');
  const [itemCost, setItemCost] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const currency = user?.currency || 'USD';
  const symbol = CURRENCY_SYMBOLS[currency] || '$';

  const handleCheck = async (e) => {
    e.preventDefault();
    setLoading(true);
    setResult(null);

    try {
      const { data } = await authPost('/api/affordability', {
        item_name: itemName,
        item_cost: parseFloat(itemCost)
      });
      setResult(data);
    } catch (e) {
      console.error('Affordability check error:', e);
    }
    setLoading(false);
  };

  const formatCurrency = (amount) => {
    return `${symbol}${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'affordable':
        return {
          icon: Check,
          color: '#10B981',
          bgColor: 'bg-[#10B981]/10',
          borderColor: 'border-[#10B981]/30',
          label: 'Affordable',
          description: 'You can comfortably afford this purchase!'
        };
      case 'not_recommended':
        return {
          icon: Warning,
          color: '#F59E0B',
          bgColor: 'bg-[#F59E0B]/10',
          borderColor: 'border-[#F59E0B]/30',
          label: 'Not Recommended',
          description: 'You can afford it, but it may impact your goals.'
        };
      case 'not_affordable':
      default:
        return {
          icon: X,
          color: '#EF4444',
          bgColor: 'bg-[#EF4444]/10',
          borderColor: 'border-[#EF4444]/30',
          label: 'Not Affordable',
          description: 'This purchase would strain your budget.'
        };
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl mx-auto" data-testid="affordability-page">
      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl gradient-ai mb-4">
          <ShoppingCart size={32} weight="duotone" className="text-white" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Can I Afford This?</h1>
        <p className="text-[#9CA3AF] mt-2">Let AI analyze your finances before you buy</p>
      </div>

      {/* Form */}
      <div className="card-default p-6">
        <form onSubmit={handleCheck} className="space-y-5">
          <div className="space-y-2">
            <Label className="text-[#F9FAFB]">What do you want to buy?</Label>
            <Input
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              placeholder="e.g., New headphones, Concert tickets, Winter jacket..."
              className="input-default w-full"
              required
              data-testid="item-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-[#F9FAFB]">How much does it cost?</Label>
            <div className="flex items-center gap-2">
              <span className="text-lg font-medium text-[#9CA3AF] w-8">{symbol}</span>
              <Input
                type="number"
                step="0.01"
                value={itemCost}
                onChange={(e) => setItemCost(e.target.value)}
                placeholder="0.00"
                className="input-default flex-1"
                required
                data-testid="item-cost-input"
              />
            </div>
          </div>

          <Button 
            type="submit" 
            disabled={loading || !itemName || !itemCost}
            className="btn-ai w-full h-12"
            data-testid="check-affordability-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Analyzing your finances...
              </>
            ) : (
              <>
                <Sparkle size={20} className="mr-2" />
                Check Affordability
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Result */}
      {result && (
        <div className="space-y-4" data-testid="affordability-result">
          {/* Status Card */}
          {(() => {
            const config = getStatusConfig(result.affordability_status);
            const StatusIcon = config.icon;
            
            return (
              <div className={`card-default p-6 border ${config.borderColor}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${config.bgColor}`}>
                    <StatusIcon size={32} weight="bold" style={{ color: config.color }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold" style={{ color: config.color }}>
                      {config.label}
                    </p>
                    <p className="text-[#9CA3AF]">{config.description}</p>
                  </div>
                </div>
                
                <div className="text-lg text-[#F9FAFB]">
                  <span className="font-semibold">{result.item_name}</span>
                  <span className="text-[#9CA3AF]"> for </span>
                  <span className="font-semibold">{formatCurrency(result.item_cost)}</span>
                </div>
              </div>
            );
          })()}

          {/* Financial Impact */}
          <div className="card-default p-5">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Wallet size={20} className="text-[#3B82F6]" />
              Financial Impact
            </h3>
            
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="card-secondary p-3">
                <p className="text-xs text-[#9CA3AF]">Current Budget</p>
                <p className="text-lg font-semibold tabular-nums text-[#F9FAFB]">
                  {formatCurrency(result.remaining_budget)}
                </p>
              </div>
              <div className="card-secondary p-3">
                <p className="text-xs text-[#9CA3AF]">After Purchase</p>
                <p className={`text-lg font-semibold tabular-nums ${
                  (result.after_purchase || 0) >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'
                }`}>
                  {formatCurrency(result.after_purchase)}
                </p>
              </div>
            </div>

            {/* Budget usage bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-[#9CA3AF]">Budget Usage After Purchase</span>
                <span className="text-[#F9FAFB]">
                  {result.total_budget > 0 
                    ? Math.round(((result.monthly_expenses + result.item_cost) / result.total_budget) * 100)
                    : 0
                  }%
                </span>
              </div>
              <div className="relative h-2 bg-[#1F2937] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#3B82F6] rounded-full transition-all"
                  style={{ 
                    width: `${Math.min(100, result.total_budget > 0 
                      ? ((result.monthly_expenses) / result.total_budget) * 100 
                      : 0
                    )}%` 
                  }}
                />
                <div 
                  className="absolute top-0 h-full bg-[#F59E0B] rounded-full transition-all"
                  style={{ 
                    left: `${Math.min(100, result.total_budget > 0 
                      ? ((result.monthly_expenses) / result.total_budget) * 100 
                      : 0
                    )}%`,
                    width: `${Math.min(100 - (result.total_budget > 0 
                      ? ((result.monthly_expenses) / result.total_budget) * 100 
                      : 0
                    ), (result.item_cost / result.total_budget) * 100)}%`
                  }}
                />
              </div>
              <div className="flex justify-between text-xs text-[#9CA3AF]">
                <span>Current expenses</span>
                <span>+ This purchase</span>
              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="card-secondary p-3">
              <div className="flex items-center gap-1 text-[#9CA3AF] text-xs mb-1">
                <TrendDown size={12} />
                <span>Monthly Income</span>
              </div>
              <p className="font-semibold tabular-nums text-[#10B981]">
                {formatCurrency(result.monthly_income)}
              </p>
            </div>
            <div className="card-secondary p-3">
              <div className="flex items-center gap-1 text-[#9CA3AF] text-xs mb-1">
                <TrendDown size={12} />
                <span>Monthly Expenses</span>
              </div>
              <p className="font-semibold tabular-nums text-[#EF4444]">
                {formatCurrency(result.monthly_expenses)}
              </p>
            </div>
            <div className="card-secondary p-3">
              <div className="flex items-center gap-1 text-[#9CA3AF] text-xs mb-1">
                <Clock size={12} />
                <span>Days Remaining</span>
              </div>
              <p className="font-semibold tabular-nums">
                {result.days_remaining} days
              </p>
            </div>
            <div className="card-secondary p-3">
              <div className="flex items-center gap-1 text-[#9CA3AF] text-xs mb-1">
                <Target size={12} />
                <span>Savings Target</span>
              </div>
              <p className={`font-semibold tabular-nums ${result.savings_impact ? 'text-[#F59E0B]' : 'text-[#10B981]'}`}>
                {formatCurrency(result.savings_target)}
              </p>
            </div>
          </div>

          {/* Savings Impact Warning */}
          {result.savings_impact && (
            <div className="flex items-start gap-3 p-4 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-xl">
              <Warning size={20} className="text-[#F59E0B] mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-[#F59E0B]">Impact on Savings Goals</p>
                <p className="text-sm text-[#F59E0B]/80 mt-1">
                  This purchase would affect your ability to meet your monthly savings target of {formatCurrency(result.savings_target)}.
                </p>
              </div>
            </div>
          )}

          {/* Buffer Warning */}
          {result.buffer_after_purchase !== undefined && result.buffer_after_purchase < 0 && (
            <div className="flex items-start gap-3 p-4 bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-xl">
              <Warning size={20} className="text-[#EF4444] mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-[#EF4444]">Risk of Running Out</p>
                <p className="text-sm text-[#EF4444]/80 mt-1">
                  Based on your spending rate, you might not have enough for the rest of the month after this purchase.
                </p>
              </div>
            </div>
          )}

          {/* AI Analysis */}
          <div 
            className="relative bg-[#111827] border border-[#8B5CF6]/30 rounded-2xl p-5 overflow-hidden"
            data-testid="ai-analysis"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#8B5CF6]/10 to-transparent pointer-events-none" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg gradient-ai flex items-center justify-center">
                  <Sparkle size={16} className="text-white" />
                </div>
                <p className="font-semibold">AI Analysis</p>
              </div>
              <div className="text-[#F9FAFB] leading-relaxed whitespace-pre-line">
                {result.ai_analysis}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Examples */}
      {!result && (
        <div className="card-secondary p-4">
          <p className="text-sm text-[#9CA3AF] text-center">
            <span className="font-medium text-[#F9FAFB]">Try checking:</span> New laptop ($800), Concert tickets ($75), Gym membership ($30/month), or Coffee machine ($150)
          </p>
        </div>
      )}
    </div>
  );
}
